import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { dateLocale } from '../../i18n/locale';
import { localize } from '../../i18n/script';
import { waitlistApi, type WaitlistMineEntry } from '../../services/waitlistApi';
import Card from '../ui/Card';

import { waitlistErrorText } from './waitlistError';

/**
 * "我的候补" tab content. Lists active waitlist entries with leave buttons.
 * Pulls a fresh Cognito token on mount so we don't depend on a parent prop.
 */
export default function WaitlistTab() {
  const { t, i18n } = useTranslation('waitlist');
  const navigate = useNavigate();

  const [entries, setEntries] = useState<WaitlistMineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        setError(t('signInFirst'));
        setLoading(false);
        return;
      }
      const list = await waitlistApi.getMine(token);
      setEntries(list);
    } catch (err) {
      setError(waitlistErrorText(err, i18n, 'tab.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLeaveConfirm = async () => {
    if (!confirmingId) return;
    const id = confirmingId;
    setBusyId(id);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        setError(t('signInFirst'));
        return;
      }
      await waitlistApi.leave(token, id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setConfirmingId(null);
    } catch (err) {
      setError(waitlistErrorText(err, i18n, 'tab.leaveFailed'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-charcoal/60">{t('loading')}</div>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return <div className="py-12 text-center text-charcoal/60">{t('tab.empty')}</div>;
  }

  const fmt = dateLocale(i18n.language);

  return (
    <div className="space-y-4">
      {entries.map((entry) => {
        const title = localize(
          entry.session.activity.titleCN,
          entry.session.activity.titleEN,
          i18n.language
        );
        const startDate = new Date(entry.session.startTime);
        const dateLabel = startDate.toLocaleDateString(fmt, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        const timeLabel = startDate.toLocaleTimeString(fmt, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        const statusLabel =
          entry.status === 'NOTIFIED'
            ? t('tab.notified')
            : t('tab.waiting', { count: entry.position });

        return (
          <Card key={entry.id} padding="lg">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* Activity Image */}
              <div className="flex-shrink-0 w-full md:w-32 h-32 bg-gray-200 rounded-lg overflow-hidden">
                {entry.session.activity.coverImageUrl ? (
                  <img
                    src={entry.session.activity.coverImageUrl}
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500">{t('tab.noImage')}</span>
                  </div>
                )}
              </div>

              {/* Waitlist Details */}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

                <div className="space-y-1 text-sm mb-4">
                  <div className="text-gray-700">
                    {dateLabel} · {timeLabel}
                  </div>
                  <div className="text-gray-700">
                    {t('tab.party', {
                      children: entry.childrenCount,
                      adults: entry.adultsCount,
                    })}
                  </div>
                  <div
                    className={entry.status === 'NOTIFIED' ? 'text-primary-dark' : 'text-gray-700'}
                  >
                    {statusLabel}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/activity/${entry.session.activity.slug}`)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    {t('viewActivity')}
                  </button>
                  <button
                    onClick={() => setConfirmingId(entry.id)}
                    disabled={busyId === entry.id}
                    className="rounded-lg bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                    type="button"
                  >
                    {busyId === entry.id ? t('working') : t('tab.leave')}
                  </button>
                </div>
              </div>
            </div>
          </Card>
        );
      })}

      {confirmingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
            <div className="px-6 py-5">
              <h2 className="text-lg font-semibold text-charcoal">{t('tab.leaveTitle')}</h2>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{t('tab.leaveBody')}</p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <button
                onClick={() => setConfirmingId(null)}
                disabled={busyId === confirmingId}
                className="rounded-lg px-4 py-2 text-sm font-medium text-charcoal hover:bg-soft-gray/50 disabled:opacity-50"
                type="button"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleLeaveConfirm}
                disabled={busyId === confirmingId}
                className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                type="button"
              >
                {busyId === confirmingId ? t('tab.leaving') : t('tab.confirmLeave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
